import * as React from "react";
import PropTypes from "prop-types";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { useThemeSettings } from "./ThemeSettingsContext";

import { textFieldCustomizations } from "./customizations/textField";
import { buttonCustomizations } from "./customizations/button";
import { cardCustomizations } from "./customizations/card";
import { modalCustomizations } from "./customizations/modal";
import { dataGridToolbarCustomizations } from "./customizations/dataGridToolbar";
import { iconButtonCustomizations } from "./customizations/iconButton";
import { dataGridCustomizations } from "./customizations/dataGrid";
import { drawerCustomizations } from "./customizations/drawer";
import { listItemIconCustomizations } from "./customizations/listItemIcon";

function AppTheme({ children }) {
  const { darkMode, fontSize } = useThemeSettings();
  const theme = React.useMemo(
    () =>
      createTheme({
        palette: {
          mode: darkMode ? "dark" : "light",
          primary: {
            main: "#2563EB", // Professional blue
            light: "#60A5FA",
            dark: "#003B7A",
            contrastText: "#FFFFFF",
          },
          secondary: {
            main: "#4B5563", // Professional gray
            light: "#9CA3AF",
            dark: "#374151",
            contrastText: "#FFFFFF",
          },
          background: {
            default: darkMode ? "#181a1b" : "#F9FAFB",
            paper: darkMode ? "#23272f" : "#FFFFFF",
            button: darkMode ? "#003B7A" : "#60A5FA",
            buttonGrey: darkMode ? "#374151" : "#f1f1f1",
          },
          text: {
            primary: darkMode ? "#fff" : "#111827",
            secondary: darkMode ? "#b0b0b0" : "#4B5563",
          },
          error: {
            main: "#DC2626",
            light: "#EF4444",
            dark: "#B91C1C",
          },
          success: {
            main: "#059669",
            light: "#10B981",
            dark: "#047857",
          },
          warning: {
            main: "#D97706",
            light: "#F59E0B",
            dark: "#B45309",
          },
          info: {
            main: "#2563EB",
            light: "#60A5FA",
            dark: "#1E40AF",
          },
        },
        shape: { 
          borderRadius: 8,
        },
        typography: {
          fontFamily: `"Inter", "Roboto", "Segoe UI", "Helvetica Neue", sans-serif`,
          fontSize: fontSize,
          button: {
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.95rem",
            letterSpacing: "0.01em",
          },
          body1: {
            fontSize: "0.95rem",
            lineHeight: 1.6,
            letterSpacing: "0.01em",
          },
          body2: {
            fontSize: "0.875rem",
            lineHeight: 1.6,
            letterSpacing: "0.01em",
          },
          h1: {
            fontSize: "2.5rem",
            fontWeight: 700,
            letterSpacing: "-0.02em",
          },
          h2: {
            fontSize: "2rem",
            fontWeight: 700,
            letterSpacing: "-0.02em",
          },
          h3: {
            fontSize: "1.75rem",
            fontWeight: 600,
            letterSpacing: "-0.01em",
          },
          h4: {
            fontSize: "1.5rem",
            fontWeight: 600,
            letterSpacing: "-0.01em",
          },
          h5: {
            fontSize: "1.25rem",
            fontWeight: 600,
            letterSpacing: "-0.01em",
          },
          h6: {
            fontSize: "1.125rem",
            fontWeight: 600,
            letterSpacing: "-0.01em",
          },
          subtitle1: {
            fontSize: "0.875rem",
            fontWeight: 500,
            letterSpacing: "0.01em",
          },
          subtitle2: {
            fontSize: "0.75rem",
            fontWeight: 500,
            letterSpacing: "0.01em",
          },
        },
        components: {
          ...textFieldCustomizations,
          ...buttonCustomizations,
          ...cardCustomizations,
          ...modalCustomizations,
          ...dataGridToolbarCustomizations,
          ...dataGridCustomizations,
          ...iconButtonCustomizations,
          ...drawerCustomizations,
          ...listItemIconCustomizations,
        },
      }),
    [darkMode, fontSize]
  );

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
}

AppTheme.propTypes = {
  children: PropTypes.node,
};

export default AppTheme;
